import React, { Fragment } from 'react';
import PropTypes from 'prop-types';
import { withRouter, Redirect, useHistory } from 'react-router-dom';
import { graphql } from 'react-apollo';
import compose from 'lodash.flowright';

import { isAlbumOwner } from '../../../../../../util/account';
import { VIDEO_TYPE } from '../../../../../../constants/videos';
import Spinner from '../../../../../../components/global/Spinner';
import VideoGrid from '../../../../../../components/grid/VideoGrid';
import VideoCard from '../../../../../pro/components/VideoCard';
import { getAsset } from '../../../../../../util/assets';
import {
  ALBUM_SLIDESHOW_STATUS,
  ALBUM_VIDEO_STATUS,
} from '../../../../../../constants/albums';
import Alert from '../../../../../../components/global/Alert';
import Button from '../../../../../../components/global/Button';
import VideoUploadIcon from '../../../../../../components/icons/VideoUploadIcon';
import WebGalleryVideoAlbumNullState from '../WebGalleryVideoAlbumNullState';
import WebGalleryActionBar from '../../../WebGalleryActionBar';
import {
  AlbumSlideshowForModeCheck,
  AlbumVideosPro,
} from '../../../../../../graphql/queries/albums';
import { createLoadMoreMethod } from '../../../../../../util/apollo';
import VideoCardActionButton from '../../../../../pro/components/VideoCard/components/VideoCardActionButton';
import { VIDEO_DOWNLOAD_LINKS_MODAL } from '../../../../../../components/grid/components/VideoDownload/modal';
import DownloadIcon from '../../../../../../components/icons/DownloadIcon';
import useOpenModal from '../../../../../../hooks/useOpenModal';
import PhotoGridViewerActionButton from '../../../../../../components/grid/components/PhotoGridViewerActionButton';
import VideoGridViewerFooter from '../../../../../../components/global/VideoGridViewerFooter';
import FeatureBlock from '../../../../../../components/contextual/FeatureBlock';
import {
  isSpecialtyAlbum,
  isWeddingsContent,
} from '../../../../../../util/album';
import SpecialtyAlbumGreetingDismissiblePersistent from '../../../../../pro/views/AlbumFolder/FreemiumEvents/SpecialtyAlbumGreetingModal/dismissiblePersistent';
import { CONTRIBUTOR_UPLOAD_ACCESS } from '../../../../../../constants/contributors';
import WebGalleryAlbumMeta from '../../../../containers/WebGalleryAlbumMeta';

const propTypes = {
  album: PropTypes.shape({
    id: PropTypes.string,
    name: PropTypes.string,
    videos: PropTypes.shape({
      total: PropTypes.number,
      records: PropTypes.arrayOf(
        PropTypes.shape({
          id: PropTypes.string,
        }),
      ),
    }),
  }),
  loading: PropTypes.bool,
  loadMore: PropTypes.func.isRequired,
  user: PropTypes.shape({
    id: PropTypes.string,
  }),
  slideshow: PropTypes.shape({
    status: PropTypes.oneOf(Object.values(ALBUM_SLIDESHOW_STATUS)),
    videos: PropTypes.shape({
      total: PropTypes.number,
    }),
  }),
};

const WebGalleryVideoAlbum = ({
  album,
  loading,
  loadMore,
  user,
  slideshow,
}) => {
  const history = useHistory();
  const openModal = useOpenModal();

  const hasVideos = (album?.unfiltered_videos?.total ?? 0) > 0;
  const videosTotal = album?.videos?.total;
  const videosRecords = album?.videos?.records ?? [];

  const isAlbumOwnerBool = isAlbumOwner(user, album);
  const greetingVideo = videosRecords.find(
    video => video.type === VIDEO_TYPE.GREETING,
  );

  if (!album) {
    return (
      <div className="d-flex p-5 w-100">
        <Spinner className="m-auto" />
      </div>
    );
  }

  if (!hasVideos) {
    return (
      <>
        <WebGalleryAlbumMeta album={album} />
        <WebGalleryActionBar album={album} />
        <WebGalleryVideoAlbumNullState album={album} user={user} />
      </>
    );
  }

  if (slideshow?.status === ALBUM_SLIDESHOW_STATUS.PUBLISHED) {
    return <Redirect to={`/albums/${album.id}/slideshow`} />;
  }

  return (
    <Fragment>
      <WebGalleryAlbumMeta album={album} />
      <WebGalleryActionBar album={album} />

      {loading && (
        <div className="d-flex p-5">
          <Spinner className="m-auto" />
        </div>
      )}
      <VideoGrid
        loading={loading}
        records={videosRecords}
        total={videosTotal}
        loadMore={loadMore}
        justifiedLayoutOptions={{
          forceAspectRatio: 1,
        }}
        videoViewerProps={{
          actions: ({ record }) =>
            record.download_enabled && (
              <PhotoGridViewerActionButton
                label="Download"
                icon={<DownloadIcon />}
                onClick={() =>
                  openModal(VIDEO_DOWNLOAD_LINKS_MODAL, {
                    video: record,
                  })
                }
              />
            ),
          footer: ({ record }) => (
            <VideoGridViewerFooter
              entity={album}
              video={record}
              isFreeWebGallery
            />
          ),
        }}
        cellRenderer={({ record: video, recordPath }) => {
          return (
            <VideoCard
              key={video.id}
              video={video}
              linkTo={recordPath}
              decorators={
                video.type === VIDEO_TYPE.GREETING ? (
                  <img
                    src={getAsset('/wedding-bells.svg')}
                    alt="wedding bells"
                    width="30"
                  />
                ) : null
              }
              actions={
                (video.download_enabled || isAlbumOwnerBool) && (
                  <VideoCardActionButton
                    onClick={() =>
                      openModal(VIDEO_DOWNLOAD_LINKS_MODAL, { video })
                    }
                    icon={<DownloadIcon />}
                  />
                )
              }
            />
          );
        }}
      />

      {!greetingVideo && isAlbumOwnerBool && isWeddingsContent(album) && (
        <Alert type="info" className="record-greeting-video-bookend">
          <div>
            <p>
              Record a greeting to welcome your guests to your Video Guestbook!
            </p>
            <Button
              onClick={() => {
                history.push(`/app/shutterbug/albums/${album.id}/record-video`);
              }}
            >
              <VideoUploadIcon color="#fff" className="mr-1" /> Record Now
            </Button>
          </div>
        </Alert>
      )}

      <FeatureBlock condition={isSpecialtyAlbum(album)}>
        <SpecialtyAlbumGreetingDismissiblePersistent
          albumId={album.id}
          onAddPhotos={() => {
            window.location.assign(album?.auto_contributor_share_url);
          }}
          enabled={
            !!album?.auto_contributor_share_url &&
            album.auto_contributor_settings.upload_access !==
              CONTRIBUTOR_UPLOAD_ACCESS.NONE
          }
        />
      </FeatureBlock>
    </Fragment>
  );
};

WebGalleryVideoAlbum.propTypes = propTypes;

const withAlbumVideos = graphql(AlbumVideosPro, {
  options: ({ album, user }) => ({
    variables: {
      id: album.id,
      sort_by_type: true,
      status: ALBUM_VIDEO_STATUS.PUBLISHED,
      uploader_ids: [],
      include_user_uploaded_videos: !!user?.id,
      user_uploaded_videos: user?.id,
    },
  }),
  props: ({ data: { loading, album, fetchMore } = {}, ownProps }) => ({
    album,
    loading: ownProps.loading || loading,
    loadMore: createLoadMoreMethod({
      source: album,
      recordsPath: 'videos',
      fetchMore,
    }),
  }),
  skip: ({ album }) => !album,
});

const withAlbumWithSlideshow = graphql(AlbumSlideshowForModeCheck, {
  options: ({ match }) => ({
    variables: {
      id: match.params.albumId,
    },
  }),
  props: ({ data: { album } }) => ({
    slideshow: album?.slideshow,
  }),
  skip: ({ match }) => !match?.params?.albumId,
});

export default compose(
  withRouter,
  withAlbumVideos,
  withAlbumWithSlideshow,
)(WebGalleryVideoAlbum);
